import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import classNames from 'clsx';

const buttonVariants = (
  variant: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'muted' | 'link' | 'warning',
  size?: 'default' | 'sm' | 'lg' | 'icon',
  isLoading?: boolean,
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full'
) => {
  // Peso del texto: medium (500) en todas las variantes, no semibold ni bold. Pedido de
  // JP (22/10/2026), igual en todas las apps; `muted` sigue en normal.
  const baseClasses =
    'frost-btn inline-flex items-center justify-center whitespace-nowrap text-fs-14 font-medium tracking-[0.005em] transition-[transform,box-shadow,background,border-color,color] duration-200 ease-frost focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost-brand disabled:pointer-events-none relative overflow-hidden appearance-none active:scale-[0.985] motion-reduce:transform-none motion-reduce:transition-none';

  const variantClasses = {
    // Un estilo por rol: el principal es el único con brillo; secundarios blancos con
    // línea fina, sin relleno gris (revamp calmo, 7/10/2026).
    default:
      'bg-frost-brand text-white shadow-frost-glow hover:bg-frost-brand-hover hover:shadow-frost-glow-hover',
    destructive: 'bg-frost-coral text-white shadow-frost-glow-coral hover:bg-frost-coral-deep',
    outline:
      'border border-frost-ink/12 bg-frost-card text-frost-ink hover:border-frost-ink/20 hover:bg-frost-card',
    secondary:
      'border border-frost-ink/12 bg-frost-card text-frost-ink hover:border-frost-ink/20 hover:bg-frost-card',
    ghost: 'bg-transparent text-frost-ink-soft hover:text-frost-ink hover:bg-frost-ink/[0.04]',
    muted:
      '!font-normal border border-frost-ink/12 bg-frost-card text-frost-ink-mid hover:border-frost-ink/20',
    link: 'text-frost-brand underline-offset-4 hover:underline',
    warning: 'bg-frost-warning text-white shadow-frost-glow-warning hover:bg-frost-warning-deep'
  };

  // Escala de JP (7/10/2026): todo botón va en 8 px (`--radius-frost-button`); `sm`,
  // `md` y `lg` son alias del mismo radio. `full` solo sigue siendo un círculo cuando es
  // un botón de ícono sin texto (`size="icon"`); con texto también pasa a 8 px.
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-frost-button',
    md: 'rounded-frost-button',
    lg: 'rounded-frost-button',
    full: size === 'icon' ? 'rounded-full' : 'rounded-frost-button'
  };

  const sizeClasses = {
    default: 'min-h-[40px] h-[40px] px-4',
    sm: 'min-h-[34px] h-[34px] px-3 text-fs-12-5',
    lg: 'min-h-[48px] h-[48px] px-6 text-sm',
    icon: 'min-h-[40px] h-[40px] w-[40px]'
  };

  return classNames(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size || 'default'],
    // Pendiente: el botón conserva su color (el spinner es la señal); solo el
    // deshabilitado de verdad pasa a gris: lavado gris, texto apagado, sin línea ni
    // brillo (revamp calmo). El link deshabilitado queda sin caja.
    isLoading
      ? 'cursor-wait'
      : variant === 'link' || variant === 'ghost'
        ? 'disabled:text-frost-ink-whisper'
        : 'disabled:border-transparent disabled:bg-frost-ink/4 disabled:text-frost-ink-whisper disabled:shadow-none',
    roundedClasses[rounded || 'md']
  );
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'muted'
    | 'link'
    | 'warning'
    /** @deprecated legacy alias mapped to 'default' */
    | 'filled'
    /** @deprecated legacy alias mapped to 'outline' */
    | 'outlined'
    /** @deprecated legacy alias mapped to 'default' */
    | 'cta';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  isLoading?: boolean;
  /** @deprecated use `isLoading` */
  loading?: boolean;
  /** @deprecated use `className="w-full"` */
  fullWidth?: boolean;
}

const LEGACY_VARIANT_MAP: Record<string, ButtonProps['variant']> = {
  filled: 'default',
  outlined: 'outline',
  cta: 'default'
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      rounded = 'md',
      asChild = false,
      isLoading = false,
      loading,
      fullWidth,
      disabled = false,
      onClick,
      ...props
    },
    ref
  ) => {
    const effectiveLoading = isLoading || !!loading;
    type CoreVariant =
      'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'muted' | 'link' | 'warning';
    const effectiveVariant = (LEGACY_VARIANT_MAP[variant as string] ?? variant) as CoreVariant;
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (effectiveLoading) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const mergedClassName = classNames(
      buttonVariants(effectiveVariant, size, effectiveLoading, rounded),
      fullWidth && 'w-full',
      className,
      effectiveLoading && 'relative',
      'touch-manipulation'
    );

    if (asChild) {
      // Slot requires exactly one React child — forward `children` untouched so the
      // consumer-supplied element (e.g. <Link>) receives className/ref/onClick.
      return (
        <Slot className={mergedClassName} ref={ref} onClick={handleClick} {...props}>
          {props.children}
        </Slot>
      );
    }

    return (
      <button
        className={mergedClassName}
        ref={ref}
        onClick={handleClick}
        {...props}
        disabled={effectiveLoading || disabled}
        aria-busy={effectiveLoading || undefined}
      >
        {/* Pendiente: spinner junto al texto, que sigue visible. Hay botones que cambian
            su etiqueta mientras guardan ("Guardando…") y esa etiqueta se tiene que leer. */}
        {/* Los dos spans heredan el gap del botón (0.5em por defecto, o el `gap-*` del
            className): así el ícono y el texto se separan solos. El margen propio de un
            ícono suelto se anula para no sumar dos separaciones. */}
        <span className="inline-flex items-center gap-[inherit]">
          {effectiveLoading && (
            <span
              aria-hidden
              className="size-[1em] shrink-0 rounded-full border-2 border-current/25 border-t-current animate-spin motion-reduce:animate-[spin_1.6s_linear_infinite]"
            />
          )}
          <span
            className={classNames(
              'inline-flex items-center gap-[inherit] [&>svg]:mx-0',
              effectiveLoading && 'opacity-80'
            )}
          >
            {props.children}
          </span>
        </span>
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
